<template>
  <LcLotteryEditorCard
    v-model:expanded="isExpanded"
    v-bind="{ removable, numberClass }"
    :[EDITOR_PLAY_ID_ATTRIBUTE]="play.id"
    ref="card"
    :clear-disabled="isClearDisabled"
    :fields="cardFields"
    :complete="remaining <= 0"
    :bet-type="betTypeTranslated"
    :done-label="$t('picklottery.remainingnumbers.done')"
    :quick-pick-label="$t('picklottery.playslip.button.quickpick')"
    radius="medium"
    :clear-label="$t('picklottery.playslip.button.clear')"
    :remove-aria-label="$t('editor.card.remove.arialabel')"
    :wager="toStakeMoney(selectedStake)"
    :warning="warning"
    @clear="handleClear"
    @remove="store.discardPlay(play.id)"
    @quick-pick="handleQuickpick"
    @collapsed="notifyCollapsed(play.id)"
    @expanded="scrollExpandedIntoView(cardRef)"
    @update:expanded="handleExpandedChange"
  >
    <template #headline="{ countClass }">
      <EditorRemainingHeadline
        v-bind="{ countClass }"
        keypath="picklottery.remainingnumbers"
        :name="$t('picklottery.remainingnumbers.base', visualRemaining)"
        :remaining="visualRemaining"
      />
    </template>

    <LcForm
      :ref="setFormRef"
      class="flex flex-col gap-100"
      :tracking="{ containerId: 'pick_lottery_form' }"
    >
      <EditorPickLotteryBetTypeField v-bind="{ config, lotteryId, play }" />

      <EditorPickLotteryWagerField
        v-if="hasMultipleStakes"
        v-bind="{ config, lotteryId, play, priceList }"
      />

      <EditorPickLotteryNumbersField v-bind="{ config, lotteryId, play }" />
    </LcForm>
  </LcLotteryEditorCard>
</template>

<script lang="ts" setup>
import type { LcLotteryNumbersProps } from '@lottocom/frontend-components'
import type { ComponentPublicInstance } from 'vue'

import * as Yup from 'yup'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, PriceList } from '../@types/editor'

import { EDITOR_PLAY_ID_ATTRIBUTE } from '../constants/editor'
import { EDITOR_QUICK_PICK_MOTION } from '../constants/quickPickAnimation'
import { type EditorPlay, usePickLotteryStore } from '../stores/pickLottery'
import { buildCardFields, getSelectedNumbers } from '../utils/editorFields'
import { validatePlay } from '../utils/editorPlay'

type FormValues = Partial<{ addOn: Maybe<string>; numbers: Maybe<number>[] }>

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
  priceList: PriceList
}

const { config, lotteryId, play, priceList } = defineProps<Props>()

const { getPerPlayTranslation } = useTranslations()
const { getTranslation, t } = useI18nUtils()
const store = usePickLotteryStore(lotteryId)
const { isDisabled } = usePickLotteryDisabledFields(
  lotteryId,
  () => config,
  () => play,
)
const { scrollExpandedIntoView, scrollExpandingPlayIntoView } = useExpandableScroll()
const {
  cancel,
  getDisplayPlay,
  getNumberState,
  getSummaryPlay,
  isAnimating,
  notifyCollapsed,
  triggerSinglePlay,
} = useEditorQuickPickAnimation()

const displayPlay = computed(() => getDisplayPlay(play))

const cardRef = useTemplateRef<ComponentPublicInstance>('card')

const { selectedStake } = usePickLotteryWager(
  lotteryId,
  () => config,
  () => priceList,
  () => play,
)

const { setFormRef: registerFormRef, validate } = useForm<FormValues>(
  Yup.object().shape({
    addOn: Yup.string().required(),
    numbers: Yup.array()
      .default([])
      .test(
        'allowed-combinations',
        getTranslation({
          fallback: t(`lottery.validation.field.numbers.notvalidforbettype`),
          key: `lottery.${lotteryId}.validation.field.numbers.notvalidforbettype`,
        }),
        (value) => validatePlay(lotteryId, config, play.perPlayAddOn?.value ?? null, value),
      ),
  }),
)

const betTypeTranslated = computed(() =>
  getPerPlayTranslation(lotteryId, play.perPlayAddOn?.name, play.perPlayAddOn?.value),
)

const cardFields = computed(() =>
  buildCardFields(config, getSummaryPlay(play), (field) => ({
    ignored: isDisabled(field?.name ?? ''),
  })),
)

const currentValues = computed<FormValues>(() => ({
  addOn: play.perPlayAddOn?.value ?? '',
  numbers: numbers.value,
}))

const hasMultipleStakes = computed(() => config.stakes.length > 1)

const isClearDisabled = computed(() => !play.fields.some(({ selected }) => selected.length > 0))

const isExpanded = computed({
  get: () => store.expandedPlayId === play.id,
  set: (expanded) => store.setExpandedPlay(expanded ? play.id : null),
})

const numbers = computed(() => getSelectedNumbers(play.fields))

const removable = computed(
  () => isExpanded.value && store.isPlayEmpty(play) && store.plays.length > 1,
)

const remaining = computed(() =>
  config.numbers.reduce((acc, { name, selectableMax }, index) => {
    if (isDisabled(name)) return acc
    const selectedCount = play.fields[index]?.selected.length ?? 0
    return acc + Math.max(0, selectableMax - selectedCount)
  }, 0),
)

const visualRemaining = useEditorVisualRemaining(() => remaining.value)

// incomplete plays are flagged only while collapsed, invalid ones also replace the done headline
const warning = computed(() => {
  if (store.isPlayEmpty(play)) return undefined
  if (remaining.value > 0) return isExpanded.value ? undefined : t('editor.play.badge.incomplete')
  return store.isPlayValid(config, play) ? undefined : t('editor.play.badge.invalid')
})

const numberClass: LcLotteryNumbersProps['numberClass'] = (value, name) => {
  const state = getNumberState(play.id, value, name)
  return state ? `pick-lottery-ball-${state}` : ''
}

const handleClear = () => {
  if (isClearDisabled.value || isAnimating(play.id)) return
  store.clearPlay(play.id, config)
}

const handleExpandedChange = (expanded: boolean) => {
  cancel(play.id)
  if (!expanded) return
  scrollExpandingPlayIntoView(cardRef.value)
}

const handleQuickpick = () => {
  triggerSinglePlay({
    config,
    ignoredFields: store.getIgnoredFieldNames(config, play),
    kind: 'digit',
    play,
    quickPick: () => store.quickPick(play.id, config),
    replaceAll: store.isPlayComplete(config, play),
  })
}

const invokeValidate = () => validate(currentValues.value)

const setFormRef = (formRef: unknown) => {
  registerFormRef(formRef)
  if (formRef) nextTick(invokeValidate)
}

watchDeep([() => play.fields, () => play.perPlayAddOn], invokeValidate)

const quickPickMotion = EDITOR_QUICK_PICK_MOTION
</script>

<style lang="scss" scoped>
$roll-tick: v-bind("quickPickMotion.tick + 'ms'");
$settle-duration: v-bind("quickPickMotion.settle + 'ms'");

/* stylelint-disable selector-class-pattern */
:deep(.pick-lottery-ball-rolling) {
  overflow: hidden;

  .lc-lottery-numbers__value {
    animation: pick-lottery-ball-roll $roll-tick linear infinite;
  }
}

:deep(.pick-lottery-ball-locked) {
  animation: pick-lottery-ball-settle $settle-duration $transition-ease-pop;
}
/* stylelint-enable selector-class-pattern */

@keyframes pick-lottery-ball-roll {
  from {
    opacity: 0.35;
    transform: translateY(-55%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pick-lottery-ball-settle {
  from {
    transform: scale(1.07);
  }

  to {
    transform: scale(1);
  }
}
</style>
